// UK Template H: the OnPay look (blue bars, grey sub-headers, Courier grid,
// grey footer) arranged as a UK payslip: tax period, tax code, NI number
// and NI letter in the header; Payments, Deductions (PAYE, National
// Insurance, student loan, pension and the rest in one column) and a
// year-to-date box; totals; BACS and the employer in the footer. Dates are
// DD/MM/YYYY, money is in pounds, year to date runs from 6 April.

import { describePayment } from "./ukPaymentMethods";

// Deterministic hash so generated numbers are stable across a run's payslips
function stableHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h);
}

export function generateUkTemplateH(doc, data, pageWidth, pageHeight, margin) {
  const {
    formData, hours, overtime, commission = 0, regularPay, overtimePay, grossPay,
    payeTax, nationalInsurance, studentLoan, totalTax, rate,
    startDate, endDate, payDate, stubNum,
    ytdGrossPay = grossPay, ytdPayeTax = payeTax, ytdNationalInsurance = nationalInsurance, ytdStudentLoan = studentLoan,
    ytdRegularPay = regularPay, ytdOvertimePay = overtimePay, ytdCommission = 0,
    deductionsData = [], totalDeductions = 0, contributionsData = [], totalContributions = 0,
    ytdDeductions = 0, ytdContributions = 0, ytdPayPeriods = 1,
    payType = "hourly", isContractor = false,
    taxCode = "", niNumber = "", niCategory = "A", taxPeriod = "", taxYear = "",
    periodCheckNumber = "", periodMemo = "",
    absencePlansData = []
  } = data;

  // Colour scheme, as the US OnPay template
  const colors = {
    blue: [37, 128, 216],
    lightBlue: [215, 215, 215],
    lightGray: [240, 240, 240],
    borderGray: [200, 200, 200],
    white: [255, 255, 255],
    black: [0, 0, 0],
  };

  const fmtCurrency = (n) => Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = (date) => {
    let mm, dd, yyyy;
    if (typeof date === "string" && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = date.split("-").map(Number);
      yyyy = parts[0]; mm = String(parts[1]).padStart(2, "0"); dd = String(parts[2]).padStart(2, "0");
    } else if (date instanceof Date) {
      mm = String(date.getUTCMonth() + 1).padStart(2, "0"); dd = String(date.getUTCDate()).padStart(2, "0"); yyyy = date.getUTCFullYear();
    } else {
      const d = new Date(date);
      mm = String(d.getMonth() + 1).padStart(2, "0"); dd = String(d.getDate()).padStart(2, "0"); yyyy = d.getFullYear();
    }
    return `${dd}/${mm}/${yyyy}`;
  };

  // The slip adds up on its own: every listed deduction comes off gross pay
  const otherDeductions = (Number(totalDeductions) || 0) + (Number(totalContributions) || 0);
  const totalAllDeductions = (Number(totalTax) || 0) + otherDeductions;
  const net = (Number(grossPay) || 0) - totalAllDeductions;
  const ytdTotalTax = (Number(ytdPayeTax) || 0) + (Number(ytdNationalInsurance) || 0) + (Number(ytdStudentLoan) || 0);
  const ytdOther = (Number(ytdDeductions) || 0) + (Number(ytdContributions) || 0);
  const ytdNet = (Number(ytdGrossPay) || 0) - ytdTotalTax - ytdOther;
  // the payslip number doubles as the cheque number when paid by cheque
  const payslipNo = periodCheckNumber || String(1 + (stableHash((formData.name || "emp") + stubNum) % 999));
  // how it was paid: the badge, banner and payment box follow the method,
  // and only a bank method names an account
  const pm = describePayment(formData.paymentMethod, { bankLast4: formData.bank || "", chequeNo: payslipNo, paidOn: formatDate(payDate) });

  const m = 15;
  let y = 12;

  // ==================== TOP: NAME + BACS BADGE ====================
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...colors.black);
  doc.text(formData.name || "Employee Name", m + 50, y + 5);

  doc.setFillColor(...colors.blue);
  doc.rect(pageWidth - m - 35, y - 2, 40, 12, "F");
  doc.setTextColor(...colors.white);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(pm.badge, pageWidth - m - 15, y + 6, { align: "center" });

  y += 15;

  // ==================== BANNER ====================
  doc.setFillColor(...colors.blue);
  doc.rect(m, y, pageWidth - 2 * m, 20, "F");
  doc.setTextColor(...colors.white);
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(`${pm.banner} *** ${pm.banner} ` + "*".repeat(90), m + 10, y + 14);

  y += 16;

  // ==================== ADDRESS BLOCK ====================
  y += 20;
  doc.setFillColor(...colors.white);
  doc.rect(m, y, pageWidth - 2 * m, 35, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...colors.black);
  doc.text(formData.name || "Employee Name", m + 5, y + 10);
  doc.text(formData.address || "1 High Street", m + 5, y + 20);
  doc.text(`${formData.city || "Town"} ${formData.postcode || "SW1A 1AA"}`, m + 5, y + 30);

  // the employer, where a US stub prints its VOID mark
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(formData.company || "Employer Name", pageWidth - m - 5, y + 10, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(formData.companyAddress || "1 Business Park", pageWidth - m - 5, y + 20, { align: "right" });
  doc.text(`${formData.companyCity || "Town"} ${formData.companyPostcode || "SW1A 1AA"}`, pageWidth - m - 5, y + 30, { align: "right" });

  y += 10;
  // the memo, or a thank-you line
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(periodMemo || "Thank you for your hard work.", m + 5, y + 40);

  y += 20;
  doc.setTextColor(...colors.black);
  y += 40;

  // ==================== PAYSLIP INFO BAR ====================
  doc.setFillColor(...colors.white);
  doc.rect(m, y, pageWidth - 2 * m, 36, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const blueBoxWidth = 155;
  doc.setFillColor(...colors.blue);
  doc.rect(m + 2, y + 1, blueBoxWidth, 12, "F");
  doc.setTextColor(...colors.white);
  doc.text(formData.name || "Employee Name", m + 6, y + 10);
  doc.setTextColor(...colors.black);

  y += 5;
  doc.setFontSize(7);
  const payrollNo = formData.employeeId || String(1000000 + (stableHash(formData.name || "emp") % 9000000));
  const label = (t, x, yy) => { doc.setFont("helvetica", "bold"); doc.text(t, x, yy); doc.setFont("helvetica", "normal"); };

  // left: the payslip and its period
  label("Payslip #:", m + 5, y + 18); doc.text(payslipNo, m + 44, y + 18);
  label("Pay Date:", m + 90, y + 18); doc.text(formatDate(payDate), m + 145, y + 18);
  label("Period Start:", m + 5, y + 30); doc.text(formatDate(startDate), m + 50, y + 30);
  label("Period Ending:", m + 90, y + 30); doc.text(formatDate(endDate), m + 145, y + 30);

  // right: what HMRC needs on a payslip
  const rx = pageWidth - m - 330;
  const rx2 = pageWidth - m - 165;
  label("Tax Period:", rx, y + 9); doc.text(`${taxPeriod || ""}${taxYear ? `  (${taxYear})` : ""}`.trim(), rx + 42, y + 9);
  label("Payroll No:", rx, y + 18); doc.text(payrollNo, rx + 42, y + 18);
  label("NI Number:", rx, y + 30); doc.text(niNumber || formData.niNumber || "", rx + 42, y + 30);
  label("Pay Method:", rx2, y + 9); doc.text(pm.label, rx2 + 46, y + 9);
  label("Tax Code:", rx2, y + 18); doc.text(taxCode || formData.taxCode || "1257L", rx2 + 42, y + 18);
  label("NI Letter:", rx2, y + 30); doc.text(isContractor ? "n/a" : (niCategory || "A"), rx2 + 42, y + 30);

  y += 40;

  // ==================== MAIN TABLES ====================
  const tableWidth = pageWidth - 2 * m;
  const col1Width = tableWidth * 0.40;  // Payments
  const col2Width = tableWidth * 0.32;  // Deductions
  const col3Width = tableWidth * 0.28;  // Year to date
  const col1X = m;
  const col2X = m + col1Width;
  const col3X = m + col1Width + col2Width;
  const headerHeight = 12;
  const subHeaderHeight = 10;
  const rowHeight = 8;
  const numRows = 25;

  // a blue header, a grey sub-header with black sides
  const tableHead = (x, w, title) => {
    doc.setFillColor(...colors.blue);
    doc.rect(x, y, w, headerHeight, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...colors.white);
    doc.text(title, x + w / 2, y + 8, { align: "center" });
    const ty = y + headerHeight;
    doc.setFillColor(...colors.lightBlue);
    doc.rect(x, ty, w, subHeaderHeight, "F");
    doc.setDrawColor(...colors.borderGray);
    doc.rect(x, ty, w, subHeaderHeight);
    doc.setDrawColor(...colors.black);
    doc.line(x, ty, x, ty + subHeaderHeight);
    doc.line(x + w, ty, x + w, ty + subHeaderHeight);
    doc.setFontSize(6);
    doc.setTextColor(...colors.black);
    doc.setFont("courier", "normal");
    return ty;
  };
  // vertical dividers after each column but the last, black outer frame
  const tableFrame = (x, w, widths, top, bottom) => {
    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);
    let dx = x;
    widths.slice(0, -1).forEach((cw) => { dx += cw; doc.line(dx, top, dx, bottom); });
    doc.setDrawColor(...colors.black);
    doc.line(x, top, x, bottom);
    doc.line(x + w, top, x + w, bottom);
    doc.line(x, bottom, x + w, bottom);
  };
  const pad = (rows, cols) => { while (rows.length < numRows) rows.push(new Array(cols).fill("")); return rows.slice(0, numRows); };

  // ── Payments ──
  let tableY = tableHead(col1X, col1Width, "Payments");
  const gw = { desc: col1Width * 0.28, hours: col1Width * 0.16, rate: col1Width * 0.18, amt: col1Width * 0.18, ytd: col1Width * 0.20 };
  let xPos = col1X + 2;
  doc.text("Description", xPos, tableY + 7);
  xPos += gw.desc; doc.text("Hours", xPos + gw.hours / 2, tableY + 7, { align: "center" });
  xPos += gw.hours; doc.text("Rate", xPos + gw.rate / 2, tableY + 7, { align: "center" });
  xPos += gw.rate; doc.text("Amount", xPos + gw.amt / 2, tableY + 7, { align: "center" });
  xPos += gw.amt; doc.text("YTD", xPos + gw.ytd / 2, tableY + 7, { align: "center" });
  tableY += subHeaderHeight;

  const hourly = payType !== "salary";
  const earningsRows = pad([
    hourly
      ? ["Basic Pay", hours > 0 ? Number(hours).toFixed(2) : "0.00", rate > 0 ? fmtCurrency(rate) : "0.00", fmtCurrency(regularPay), fmtCurrency(ytdRegularPay)]
      : ["Salary", "", "", fmtCurrency(regularPay), fmtCurrency(ytdRegularPay)],
    ...(hourly && !isContractor ? [["Overtime", overtime > 0 ? Number(overtime).toFixed(2) : "0.00", rate > 0 ? fmtCurrency(rate * 1.5) : "0.00", fmtCurrency(overtimePay), fmtCurrency(ytdOvertimePay)]] : []),
    ...(commission > 0 ? [["Commission", "", "", fmtCurrency(commission), fmtCurrency(ytdCommission)]] : []),
  ], 5);
  const paymentsTop = tableY;
  earningsRows.forEach((row) => {
    xPos = col1X + 2;
    doc.text(row[0], xPos, tableY + 6);
    xPos += gw.desc; doc.text(row[1], xPos + gw.hours - 3, tableY + 6, { align: "right" });
    xPos += gw.hours; doc.text(row[2], xPos + gw.rate - 3, tableY + 6, { align: "right" });
    xPos += gw.rate; doc.text(row[3], xPos + gw.amt - 3, tableY + 6, { align: "right" });
    xPos += gw.amt; doc.text(row[4], xPos + gw.ytd - 5, tableY + 6, { align: "right" });
    tableY += rowHeight;
  });
  tableFrame(col1X, col1Width, [gw.desc, gw.hours, gw.rate, gw.amt, gw.ytd], paymentsTop, tableY);

  // ── Deductions: statutory first, then pension and the rest ──
  tableY = tableHead(col2X, col2Width, "Deductions");
  const wt = { desc: col2Width * 0.50, amt: col2Width * 0.25, ytd: col2Width * 0.25 };
  xPos = col2X + 2;
  doc.text("Description", xPos, tableY + 7);
  xPos += wt.desc; doc.text("Amount", xPos + wt.amt / 2, tableY + 7, { align: "center" });
  xPos += wt.amt; doc.text("YTD", xPos + wt.ytd / 2, tableY + 7, { align: "center" });
  tableY += subHeaderHeight;

  const dedRows = [];
  if (!isContractor) {
    dedRows.push(["PAYE Tax", fmtCurrency(payeTax), fmtCurrency(ytdPayeTax)]);
    dedRows.push(["National Insurance", fmtCurrency(nationalInsurance), fmtCurrency(ytdNationalInsurance)]);
    if (Number(studentLoan) > 0 || Number(ytdStudentLoan) > 0) dedRows.push(["Student Loan", fmtCurrency(studentLoan || 0), fmtCurrency(ytdStudentLoan || 0)]);
  }
  contributionsData.forEach((c) => {
    dedRows.push([String(c.name || c.type || "Pension").substring(0, 18), fmtCurrency(c.currentAmount || 0), fmtCurrency((c.currentAmount || 0) * ytdPayPeriods)]);
  });
  deductionsData.forEach((d) => {
    dedRows.push([String(d.name || d.type || "Deduction").substring(0, 18), fmtCurrency(d.currentAmount || 0), fmtCurrency((d.currentAmount || 0) * ytdPayPeriods)]);
  });
  const deductionsTop = tableY;
  pad(dedRows, 3).forEach((row) => {
    xPos = col2X + 2;
    doc.text(row[0], xPos, tableY + 6);
    xPos += wt.desc; doc.text(row[1], xPos + wt.amt - 3, tableY + 6, { align: "right" });
    xPos += wt.amt; doc.text(row[2], xPos + wt.ytd - 5, tableY + 6, { align: "right" });
    tableY += rowHeight;
  });
  tableFrame(col2X, col2Width, [wt.desc, wt.amt, wt.ytd], deductionsTop, tableY);

  // ── This tax year to date ──
  tableY = tableHead(col3X, col3Width, "Year to Date");
  const yt = { desc: col3Width * 0.58, amt: col3Width * 0.42 };
  xPos = col3X + 2;
  doc.text(taxYear ? `Tax year ${taxYear}` : "Tax year", xPos, tableY + 7);
  xPos += yt.desc; doc.text("Amount", xPos + yt.amt / 2, tableY + 7, { align: "center" });
  tableY += subHeaderHeight;

  const ytdRows = [
    ["Gross Pay", fmtCurrency(ytdGrossPay)],
    ["Taxable Pay", fmtCurrency(ytdGrossPay)],
    ...(isContractor ? [] : [
      ["PAYE Tax", fmtCurrency(ytdPayeTax)],
      ["National Insurance", fmtCurrency(ytdNationalInsurance)],
      ...(Number(ytdStudentLoan) > 0 ? [["Student Loan", fmtCurrency(ytdStudentLoan)]] : []),
    ]),
    ...(Number(ytdContributions) > 0 ? [["Pension", fmtCurrency(ytdContributions)]] : []),
    ...(Number(ytdDeductions) > 0 ? [["Other Deductions", fmtCurrency(ytdDeductions)]] : []),
    ["Net Pay", fmtCurrency(ytdNet)],
  ];
  const ytdTop = tableY;
  pad(ytdRows, 2).forEach((row) => {
    xPos = col3X + 2;
    doc.text(row[0], xPos, tableY + 6);
    xPos += yt.desc; doc.text(row[1], xPos + yt.amt - 5, tableY + 6, { align: "right" });
    tableY += rowHeight;
  });
  tableFrame(col3X, col3Width, [yt.desc, yt.amt], ytdTop, tableY);

  // ==================== TOTALS ROW ====================
  const totalsY = y + headerHeight + subHeaderHeight + (numRows * rowHeight);
  const totalsBox = (x, w) => {
    doc.setFillColor(...colors.white);
    doc.rect(x, totalsY, w, rowHeight + 2, "F");
    doc.setDrawColor(...colors.borderGray);
    doc.line(x, totalsY, x + w, totalsY);
    doc.setDrawColor(...colors.black);
    doc.line(x, totalsY, x, totalsY + rowHeight + 2);
    doc.line(x + w, totalsY, x + w, totalsY + rowHeight + 2);
    doc.line(x, totalsY + rowHeight + 2, x + w, totalsY + rowHeight + 2);
  };
  doc.setFont("courier", "bold");
  doc.setFontSize(6);
  doc.setTextColor(...colors.black);
  totalsBox(col1X, col1Width);
  doc.text("Total Gross Pay", col1X + 3, totalsY + 6);
  doc.text(fmtCurrency(grossPay), col1X + gw.desc + gw.hours + gw.rate + gw.amt - 3, totalsY + 6, { align: "right" });
  doc.text(fmtCurrency(ytdGrossPay), col1X + col1Width - 5, totalsY + 6, { align: "right" });
  totalsBox(col2X, col2Width);
  doc.text("Total Deductions", col2X + 3, totalsY + 6);
  doc.text(fmtCurrency(totalAllDeductions), col2X + wt.desc + wt.amt - 3, totalsY + 6, { align: "right" });
  doc.text(fmtCurrency(ytdTotalTax + ytdOther), col2X + col2Width - 5, totalsY + 6, { align: "right" });
  totalsBox(col3X, col3Width);
  doc.text("NET PAY", col3X + 3, totalsY + 6);
  doc.text(`£${fmtCurrency(net)}`, col3X + col3Width - 5, totalsY + 6, { align: "right" });
  doc.setFont("courier", "normal");

  // ==================== FOOTER ====================
  const footerY = totalsY + rowHeight + 8;
  const footerHeight = 50;
  const footerColWidth = (pageWidth - 2 * m) / 4;
  doc.setFillColor(...colors.lightGray);
  doc.rect(m, footerY, pageWidth - 2 * m, footerHeight, "F");

  const footerHeaders = ["Leave", pm.label, "Net Pay", "Employer"];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...colors.white);
  footerHeaders.forEach((header, i) => {
    const colX = m + i * footerColWidth;
    doc.setFillColor(...colors.blue);
    doc.rect(colX, footerY, footerColWidth, 12, "F");
    doc.text(header, colX + footerColWidth / 2, footerY + 8, { align: "center" });
  });

  const contentY = footerY + 15;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(...colors.black);

  // Leave: the absence plans when given, else holiday and sick at zero
  const leaveX = m + 3;
  if (absencePlansData && absencePlansData.length > 0) {
    let lineY = contentY + 5;
    absencePlansData.slice(0, 4).forEach((plan) => {
      const desc = (plan.description || "Holiday").substring(0, 10);
      const accrued = parseFloat(plan.accrued) || 0;
      const taken = parseFloat(plan.reduced) || 0;
      doc.text(`${desc}: ${taken.toFixed(2)} taken | ${accrued.toFixed(2)} accrued | ${(accrued - taken).toFixed(2)} remaining`, leaveX, lineY);
      lineY += 8;
    });
  } else {
    doc.text("Holiday: 0.00 taken | 0.00 accrued | 0.00 remaining", leaveX, contentY + 5);
    doc.text("Sick: 0.00 taken", leaveX, contentY + 13);
  }

  // BACS: the account and the payment
  const bacsX = m + footerColWidth + 3;
  doc.text(pm.accountLine, bacsX, contentY + 5);
  doc.text(`£${fmtCurrency(net)}`, bacsX, contentY + 13);
  doc.text(`Paid ${formatDate(payDate)}`, bacsX, contentY + 21);

  // Net pay, and the hours the law asks for when pay varies by time
  const netPayX = m + 2 * footerColWidth + 3;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(`Net Pay: £${fmtCurrency(net)}`, netPayX, contentY + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.text(pm.paidBy, netPayX, contentY + 14);
  if (hourly) doc.text(`Hours this period: ${(Number(hours) + Number(overtime || 0)).toFixed(2)}`, netPayX, contentY + 22);

  // Employer
  const companyX = m + 3 * footerColWidth + 3;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6);
  doc.text(formData.company || "Employer Name", companyX, contentY + 5);
  doc.setFont("helvetica", "normal");
  doc.text(formData.companyAddress || "1 Business Park", companyX, contentY + 13);
  doc.text(formData.companyCity || "Town", companyX, contentY + 21);
  doc.text(formData.companyPostcode || "SW1A 1AA", companyX, contentY + 29);

  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.3);
  for (let i = 1; i < 4; i++) {
    const divX = m + i * footerColWidth;
    doc.line(divX, footerY + 12, divX, footerY + footerHeight);
  }
  doc.line(m, footerY + 12, m, footerY + footerHeight);
  doc.line(pageWidth - m, footerY + 12, pageWidth - m, footerY + footerHeight);
  doc.line(m, footerY + footerHeight, pageWidth - m, footerY + footerHeight);
}
